import { css } from '@emotion/react';
import { breakpoints } from '@frontend/responsiveness';
import { classNames } from '@frontend/string';
import { theme } from '@frontend/theme';
import { Heading } from '@frontend/design-system';
import { useCareCreditTranslations } from '../../../../hooks/use-care-credit-translations';
import { CareCreditPatientData } from '../../../../types/care-credit.types';
import { formatCareCreditDataForDisplay } from '../../../../utils/format-display-care-credit-data';

const styles = {
  card: css`
    padding: ${theme.spacing(2)};
    display: flex;
    gap: ${theme.spacing(2)};
    background: ${theme.colors.neutral5};
    border-radius: ${theme.borderRadius.medium};
    flex-direction: column;
  `,
  items: css`
    display: flex;
    gap: ${theme.spacing(6)};

    @media (max-width: ${breakpoints.small.min}px) {
      flex-wrap: wrap;
      gap: ${theme.spacing(2)};
    }
  `,
  item: css`
    min-width: 90px;
    .item {
      &__label {
        font-size: ${theme.font.size.small};
        color: ${theme.colors.neutral50};
        white-space: nowrap;
      }
      &__value {
        font-size: ${theme.font.size.large};
        color: ${theme.colors.neutral90};
      }
    }
  `,
};

export type AccountDetailsCardProps = {
  careCreditData: CareCreditPatientData;
  accountHolderName: string;
  className?: string;
};
export const AccountDetailsCard = ({ careCreditData, accountHolderName, className }: AccountDetailsCardProps) => {
  const { t } = useCareCreditTranslations();

  const { formattedAmount, formattedAccountNumber } = formatCareCreditDataForDisplay(careCreditData);

  return (
    <div className={classNames('account-details-card', className)} css={styles.card}>
      <Heading level={3}>{t('Account Details')}</Heading>
      <div className='account-details-card__items' css={styles.items}>
        <div className='account-details-card__item' css={styles.item}>
          <div className='item__label'>{t('Account Holder')}</div>
          <div className='item__value'>{accountHolderName}</div>
        </div>
        <div className='account-details-card__item' css={styles.item}>
          <div className='item__label'>{t('Available Credit')}</div>
          <div className='item__value'>{formattedAmount}</div>
        </div>
        <div className='account-details-card__item' css={styles.item}>
          <div className='item__label'>{t('Account Number')}</div>
          <div className='item__value'>{formattedAccountNumber}</div>
        </div>
      </div>
    </div>
  );
};
